// The two demo organisations (outlet + recycler) plus any the visitor creates.
// GET /organization returns both demo orgs, so the sidebar switcher is the persona switch.

import {
  DEMO_OUTLET_ORG,
  DEMO_OUTLET_ORG_ID,
  DEMO_RECYCLER_ORG,
  DEMO_RECYCLER_ORG_ID,
  DEMO_SITES,
  DEMO_FACILITIES,
  daysAgo,
  iso,
  paginate,
} from '../core';
import {
  OrganizationType,
  type OrganizationPagination,
  type OrganizationRequest,
  type OrganizationSchema,
  type UpdateOrganizationRequest,
} from '../../types/organization.type';
import { uploadedObjectUrl } from './uploads';

function seedOrganizations(): OrganizationSchema[] {
  const olaya = DEMO_SITES[0];
  const sulay = DEMO_FACILITIES[0];
  return [
    {
      id: DEMO_OUTLET_ORG_ID,
      name: DEMO_OUTLET_ORG.name,
      organization_type: OrganizationType.WASTE_OUTLET,
      google_maps_url: `https://www.google.com/maps?q=${olaya.lat},${olaya.lng}`,
      data: {
        name_ar: DEMO_OUTLET_ORG.name_ar,
        email: DEMO_OUTLET_ORG.email,
        phone_number: DEMO_OUTLET_ORG.phone,
        industry: ['Transfer Station', 'Other'],
        more_info:
          'Supermarket chain with five branches in Riyadh and Jeddah and the Exit 10 distribution centre. ' +
          'Cardboard, plastics, cans, pallets and used cooking oil are separated at source and collected by Riyadh Green Recycling Co.',
        city: DEMO_OUTLET_ORG.city,
        country: DEMO_OUTLET_ORG.country,
        cr_number: DEMO_OUTLET_ORG.cr_number,
        vat_number: DEMO_OUTLET_ORG.vat_number,
      },
      logo_url: null,
      created_at: iso(daysAgo(430)),
      updated_at: iso(daysAgo(11)),
    },
    {
      id: DEMO_RECYCLER_ORG_ID,
      name: DEMO_RECYCLER_ORG.name,
      organization_type: OrganizationType.RECYCLER,
      google_maps_url: `https://www.google.com/maps?q=${sulay.lat},${sulay.lng}`,
      data: {
        name_ar: DEMO_RECYCLER_ORG.name_ar,
        email: DEMO_RECYCLER_ORG.email,
        phone_number: DEMO_RECYCLER_ORG.phone,
        industry: ['Paper', 'Plastic', 'Metal', 'Glass'],
        more_info:
          'Licensed recycler operating the Sulay Sorting Plant (120 t/day) and the Jeddah Transfer Station, ' +
          'with a fleet of eight collection trucks serving retail, hospitality, healthcare and school clients.',
        city: DEMO_RECYCLER_ORG.city,
        country: DEMO_RECYCLER_ORG.country,
        cr_number: DEMO_RECYCLER_ORG.cr_number,
        vat_number: DEMO_RECYCLER_ORG.vat_number,
      },
      logo_url: null,
      created_at: iso(daysAgo(610)),
      updated_at: iso(daysAgo(26)),
    },
  ];
}

let organizations: OrganizationSchema[] = seedOrganizations();
let createdCount = 0;

export function resetOrganizations(): void {
  organizations = seedOrganizations();
  createdCount = 0;
}

export const DEMO_ORG_IDS: readonly string[] = [DEMO_OUTLET_ORG_ID, DEMO_RECYCLER_ORG_ID];

/** The typed organisation a persona starts as (what GET /organization/{id} returns). */
export function demoOrganization(orgId: string): OrganizationSchema | undefined {
  return organizations.find((o) => o.id === orgId);
}

export function listOrganizations(query: Record<string, unknown>): OrganizationPagination {
  const name = typeof query.name === 'string' ? query.name.trim().toLowerCase() : '';
  const type = typeof query.organization_type === 'string' ? query.organization_type : '';
  // The sidebar switcher searches with the selected org's full name after a pick;
  // an exact name therefore lists every org so the persona switch stays available.
  const exact = name !== '' && organizations.some((o) => o.name.toLowerCase() === name);
  const filtered = organizations.filter(
    (o) => (!name || exact || o.name.toLowerCase().includes(name)) && (!type || o.organization_type === type),
  );
  const page = paginate(filtered, query.page, query.limit);
  return { organizations: page.items, total: page.total, page: page.page, limit: page.limit };
}

function logoFields(body: { logo_asset_id?: string | null }, current: string | null): string | null {
  if (!('logo_asset_id' in body)) return current;
  if (body.logo_asset_id == null) return null;
  return uploadedObjectUrl(body.logo_asset_id) ?? current;
}

export function createOrganization(body: OrganizationRequest): OrganizationSchema {
  createdCount += 1;
  const now = iso(new Date());
  const org: OrganizationSchema = {
    id: `00000000-0000-4000-8000-00000000b${String(createdCount).padStart(3, '0')}`,
    name: body.name?.trim() || 'New organization',
    organization_type: body.organization_type ?? OrganizationType.WASTE_OUTLET,
    google_maps_url: body.google_maps_url ?? '',
    data: { ...(body.data ?? {}) },
    logo_url: logoFields(body, null),
    created_at: now,
    updated_at: now,
  };
  organizations = [...organizations, org];
  return org;
}

export function updateOrganization(orgId: string, body: UpdateOrganizationRequest): OrganizationSchema | null {
  const current = organizations.find((o) => o.id === orgId);
  if (!current) return null;
  const next: OrganizationSchema = {
    ...current,
    ...(body.name !== undefined ? { name: body.name } : {}),
    ...(body.organization_type !== undefined ? { organization_type: body.organization_type } : {}),
    ...(body.google_maps_url !== undefined ? { google_maps_url: body.google_maps_url } : {}),
    data: body.data !== undefined ? { ...current.data, ...body.data } : current.data,
    logo_url: logoFields(body, current.logo_url),
    updated_at: iso(new Date()),
  };
  organizations = organizations.map((o) => (o.id === orgId ? next : o));
  return next;
}

/** False for unknown ids; throws nothing — demo orgs are protected by the route. */
export function deleteOrganization(orgId: string): boolean {
  const before = organizations.length;
  organizations = organizations.filter((o) => o.id !== orgId);
  return organizations.length < before;
}
